import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const LobbyPage: React.FC = () => {
    const { user, selectAccount } = useAuth();
    const { t } = useTranslation('common');
    const navigate = useNavigate();

    const handleSelect = async (accountId: string) => {
        await selectAccount(accountId);
        navigate('/');
    };

    // Unified design system (overhaul): flipped from the old dark #121212/#2cf1ff theme to the
    // shared light palette (canvas/surface/primary/Inter). Text/structure unchanged so account
    // selection (specs select by name / "Select an Account") is unaffected.
    return (
        <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
            <div className="max-w-2xl w-full">
                <div className="text-center mb-12">
                    <h1 data-testid="lobby-title" className="text-4xl font-bold text-text-strong mb-4">{t('auth.lobby.title')}</h1>
                    <p className="text-text-muted">{t('auth.lobby.subtitle')}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {user?.accounts.map((membership) => (
                        <button
                            key={membership.accountId}
                            onClick={() => handleSelect(membership.accountId)}
                            className="bg-surface p-6 rounded-2xl border border-border-neutral text-left hover:border-primary/50 hover:shadow-md transition-all group"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary font-bold text-xl">
                                    {membership.account.name.charAt(0)}
                                </div>
                                <span className="text-xs font-semibold px-3 py-1 bg-canvas rounded-full text-text-muted group-hover:bg-primary/10 group-hover:text-primary">
                                    {membership.role}
                                </span>
                            </div>
                            <h3 className="text-xl font-bold text-text-strong mb-1">{membership.account.name}</h3>
                            <p className="text-text-muted text-sm">{t('auth.lobby.enterHint')}</p>
                        </button>
                    ))}

                    <button
                        onClick={() => navigate('/signup')}
                        className="p-6 rounded-2xl border border-dashed border-border-neutral flex flex-col items-center justify-center gap-3 text-text-muted hover:border-primary hover:text-primary transition-all"
                    >
                        <div className="w-10 h-10 rounded-full border border-current flex items-center justify-center text-2xl">+</div>
                        <span className="font-medium">{t('auth.lobby.createAccount')}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LobbyPage;
